// Выбор в шапке: ресторан и, если у ресторана несколько групп, группа внутри него.
// Ресторан — торговое предприятие iiko RMS (поле Department): у сети на одном сервере (ЦО) их несколько.
// Группа — RestorauntGroup (у одного ресторана это обычно залы: «1 этаж», «2 этаж»). У сети одна группа
// может объединять несколько ресторанов, поэтому группа всегда выбирается внутри ресторана.
// В iikoCloud тот же ресторан — организация с тем же названием (поле Organization облачного OLAP).
//
// Значение выбора — строка (хранится в localStorage): «Все», «ресторан» или «ресторан␟группа».
export const RESTAURANT_FIELD = 'Department';
export const GROUP_FIELD = 'RestorauntGroup';
export const ALL = 'Все';

const SEPARATOR = '\u001f'; // в названиях iiko не встречается

export const selectionKey = (restaurant, group = null) => (group ? `${restaurant}${SEPARATOR}${group}` : restaurant);

// null — «Все»
export function parseSelection(key) {
  if (!key || key === ALL) return null;
  const [restaurant, group = null] = key.split(SEPARATOR);
  return { restaurant, group };
}

// Строка отчёта относится к выбранному ресторану (и группе)
export function matchesSelection(row, key) {
  const selection = parseSelection(key);
  if (!selection) return true;
  return row?.[RESTAURANT_FIELD] === selection.restaurant && (!selection.group || row?.[GROUP_FIELD] === selection.group);
}

const filled = (value) => value !== undefined && value !== null && value !== '';

// Итог summary iiko ровно уровня выбора: совпадает с выбором и не разбит дальше (тип оплаты, блюдо…).
// Уровни summary — префиксы группировки: {ресторан}, {ресторан, группа}, {ресторан, группа, …}
export function isSelectionTotal(levelFields, key) {
  const selection = parseSelection(key);
  if (!selection || !levelFields || typeof levelFields !== 'object') return false;
  const own = selection.group ? [RESTAURANT_FIELD, GROUP_FIELD] : [RESTAURANT_FIELD];
  return (
    matchesSelection(levelFields, key) &&
    Object.entries(levelFields).every(([field, value]) => own.includes(field) || !filled(value))
  );
}

const byName = (a, b) => a.localeCompare(b, 'ru');

// Пункты выбора по строкам отчёта (без «Все»): рестораны, под рестораном — его группы, если их больше одной.
// Один ресторан — только его группы (если их больше одной): строка ресторана повторяла бы «Все»
export function buildSelectionOptions(rows) {
  const restaurants = new Map();
  for (const row of rows || []) {
    const restaurant = row?.[RESTAURANT_FIELD];
    if (!filled(restaurant)) continue;
    if (!restaurants.has(restaurant)) restaurants.set(restaurant, new Set());
    if (filled(row[GROUP_FIELD])) restaurants.get(restaurant).add(row[GROUP_FIELD]);
  }

  const names = [...restaurants.keys()].sort(byName);
  const options = [];
  for (const restaurant of names) {
    const groups = [...restaurants.get(restaurant)].sort(byName);
    if (names.length > 1) options.push(selectionKey(restaurant));
    if (groups.length > 1) groups.forEach((group) => options.push(selectionKey(restaurant, group)));
  }
  return options;
}

// Подпись пункта в списке: группа или ресторан
export function selectionLabel(key) {
  const selection = parseSelection(key);
  if (!selection) return ALL;
  return selection.group || selection.restaurant;
}

// Группа под строкой своего ресторана — с отступом
export function isNestedOption(key, options) {
  const selection = parseSelection(key);
  return Boolean(selection?.group) && options.includes(selectionKey(selection.restaurant));
}

// Подпись на кнопке выбора: у сети группа — вместе с рестораном
export function selectionTitle(key, options) {
  const selection = parseSelection(key);
  if (!selection) return ALL;
  return isNestedOption(key, options) ? `${selection.restaurant} · ${selection.group}` : selectionLabel(key);
}

// В списке есть рестораны — это сеть (у одного ресторана в списке только его группы)
export const isChain = (options) => (options || []).some((key) => parseSelection(key) && !parseSelection(key).group);

// Фильтр для API «Кухни»: { restaurant, group } (пустые поля — без фильтра).
// Ресторан передаётся только у сети (chain — isChain): у одного ресторана фильтр по нему не нужен,
// а совпадение его названия в RMS и в iikoCloud не гарантировано
export function kitchenFilter(key, chain) {
  const selection = parseSelection(key);
  if (!selection) return {};
  return {
    ...(chain && { restaurant: selection.restaurant }),
    ...(selection.group && { group: selection.group }),
  };
}

// Пункты сети, которых нет среди ресторанов с данными (connected — названия организаций iikoCloud):
// ресторан и его группы. null (список не получен) или не сеть — ничего не отключаем
export function unavailableOptions(options, connected) {
  if (!connected || !isChain(options)) return [];
  const names = new Set(connected.map((name) => name.trim()));
  return options.filter((key) => {
    const selection = parseSelection(key);
    return selection && !names.has(selection.restaurant.trim());
  });
}
